import {
    Button,
    Card,
    Container,
    ListGroup,
    Stack,
    Table,
    Form,
    Image,
    Spinner,
    Alert,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import './prescription.scss';
import { ADD, AXE, CYL, SPH } from '../Eyetest/options';
import { useEffect, useState } from 'react';
import {
    useEyeTestByIdMutation,
    useEyeTestImageByIdMutation,
    useOpthAIdetailsMutation,
    usePrescriptionByTestIDMutation,
} from '../Eyetest/eyetestApi';
import { downloadFile, formatDate, frenchFormatDate } from '../../helper';
import LoadingComponent from '../LoadingComponent';
import config from '../../Api/config';
import { useDispatch } from 'react-redux';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Download } from 'lucide-react';
import { getSecureItem } from "../../utils/secureStorage";
const Prescription = () => {
    const { t, i18n } = useTranslation();
    const { state } = useLocation();
    const user = getSecureItem('UserData');
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [downloadType, setDownloadType] = useState(null); // 'prescription' or 'ai-report'
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [eyeTestData, setEyeTestData] = useState({});
    const [eyeTestImagesData, setEyeTestImagesData] = useState({});
    const [prescriptionPath, setPrescriptionPath] = useState('');
    const [opthAIpath, setOpthAIpath] = useState('');
    const [getEyeTest] = useEyeTestByIdMutation();
    const [getEyeTestImage] = useEyeTestImageByIdMutation();
    const [prescriptionByTestID] = usePrescriptionByTestIDMutation();
    const [opthAIdetails] = useOpthAIdetailsMutation();

    useEffect(() => {
        if (loading) {
            (async () => {
                try {
                    if (user?.userType == 'PATIENT') {
                        let { data } = await opthAIdetails({ eyeTestID: state.id });
                        setOpthAIpath(data?.opthAIReportPath);
                    }

                    const eyeTestData = await getEyeTest({ eyeTestID: state.id });
                    const eyeTestImageData = await getEyeTestImage({
                        eyeTestID: state.id,
                    });
                    
                    if (eyeTestData.error) {
                        throw new Error('Failed to fetch eye test data');
                    }
                    
                    if (eyeTestImageData.error) {
                        throw new Error('Failed to fetch eye test images');
                    }

                    setEyeTestData({
                        ...eyeTestData.data[0],
                        lastTimeWentToEyeDoc:
                            eyeTestData.data[0]?.lastTimeWentToEyeDoc == ''
                                ? ''
                                : formatDate(
                                      eyeTestData.data[0]?.lastTimeWentToEyeDoc
                                  ),
                    });
                    setEyeTestImagesData(eyeTestImageData.data);
                    setLoading(false);
                } catch (error) {
                    console.error('Error loading prescription:', error);
                    setError(t('failedToLoadPrescription'));
                    setLoading(false);
                }
            })();
        }
    }, []);

    // Universal download handler for both prescription and AI report
    const handleDownload = async (type = 'prescription') => {
        setDownloadType(type);
        setDownloadLoading(true);
        setError(null);
        setSuccess(null);

        try {
            let url = '';
            let fileName = '';

            if (type === 'prescription') {
                const { data } = await prescriptionByTestID({
                    eyeTestID: state.id,
                });
                
                if (!data?.prescriptionFileFullName) {
                    throw new Error('Prescription file not found');
                }
                
                url = data.prescriptionFileFullName;
                fileName = `prescription_${eyeTestData?.testUniqueCode || 'unknown'}_${new Date().toISOString().split('T')[0]}.pdf`;
            } else if (type === 'ai-report') {
                if (!opthAIpath) {
                    throw new Error('AI report not available');
                }
                url = opthAIpath;
                fileName = `ai_report_${eyeTestData?.testUniqueCode || 'unknown'}_${new Date().toISOString().split('T')[0]}.pdf`;
            }

            // Method 1: Try to open in new tab first (user-initiated)
            const newWindow = window.open(url, '_blank');
            
            // If popup was blocked or we want to force download
            if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
                // Method 2: Create a temporary link and trigger download
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/pdf',
                    },
                    credentials: 'include', // Include cookies if needed
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);

                // Create a temporary anchor element
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = blobUrl;
                a.download = fileName;

                // Append to body to ensure it's part of DOM
                document.body.appendChild(a);
                
                // Use setTimeout to ensure the click is registered as user-initiated
                setTimeout(() => {
                    a.click();
                    
                    // Cleanup
                    window.URL.revokeObjectURL(blobUrl);
                    document.body.removeChild(a);
                    
                    setSuccess(`${type === 'prescription' ? t('prescription') : t('aiReport')} ${t('downloadStarted')}`);
                }, 100);
            } else {
                // New tab was successfully opened
                setSuccess(`${type === 'prescription' ? t('prescription') : t('aiReport')} ${t('openedInNewTab')}`);
            }

        } catch (error) {
            console.error(`Download failed for ${type}:`, error);
            
            // User-friendly error messages
            if (error.message.includes('not found') || error.message.includes('not available')) {
                setError(type === 'prescription' ? t('prescriptionNotAvailable') : t('aiReportNotAvailable'));
            } else if (error.message.includes('HTTP error')) {
                setError(t('downloadServerError'));
            } else if (error.message.includes('Failed to fetch')) {
                setError(t('networkError'));
            } else {
                setError(t('downloadFailed'));
            }
            
            // Fallback: Try direct link as last resort
            if (type === 'prescription') {
                try {
                    const { data } = await prescriptionByTestID({
                        eyeTestID: state.id,
                    });
                    if (data?.prescriptionFileFullName) {
                        const fallbackLink = document.createElement('a');
                        fallbackLink.href = data.prescriptionFileFullName;
                        fallbackLink.target = '_blank';
                        fallbackLink.rel = 'noopener noreferrer';
                        document.body.appendChild(fallbackLink);
                        fallbackLink.click();
                        setTimeout(() => {
                            document.body.removeChild(fallbackLink);
                        }, 100);
                        
                        setSuccess(t('openedInNewTab'));
                        setError(null);
                    }
                } catch (fallbackError) {
                    console.error('Fallback also failed:', fallbackError);
                }
            }
        } finally {
            setDownloadLoading(false);
            setDownloadType(null);
            
            // Clear success/error messages after 5 seconds
            setTimeout(() => {
                setSuccess(null);
                setError(null);
            }, 5000);
        }
    };

    const handleDownloadReferenceLetter = async () => {
        // This function seems to be the same as prescription download
        // You can redirect to the main download function
        handleDownload('prescription');
    };

    if (loading) {
        return <LoadingComponent />;
    }

    return (
        <>
            <Container fluid className="mb-4">
                {/* Success and Error Alerts */}
                {success && (
                    <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                        {success}
                    </Alert>
                )}
                
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                
                <Stack direction="horizontal" gap={3}>
                    <div className="ms-auto">
                        <Button
                            variant="light"
                            onClick={() => handleDownload('prescription')}
                            disabled={downloadLoading && downloadType === 'prescription'}
                        >
                            {downloadLoading && downloadType === 'prescription' ? (
                                <Spinner
                                    animation="border"
                                    role="status"
                                    size="sm"
                                    className="me-2"
                                />
                            ) : (
                                <Download
                                    width={16}
                                    className="me-2"
                                />
                            )}
                            {t('downloadPrescription')}
                        </Button>

                        {opthAIpath && (
                            <Button 
                                variant="primary" 
                                className="ms-3"
                                onClick={() => handleDownload('ai-report')}
                                disabled={downloadLoading && downloadType === 'ai-report'}
                            >
                                {downloadLoading && downloadType === 'ai-report' ? (
                                    <Spinner
                                        animation="border"
                                        role="status"
                                        size="sm"
                                        className="me-2"
                                    />
                                ) : (
                                    <Download
                                        width={16}
                                        className="me-2"
                                    />
                                )}
                                {t('testAIAnalysisDownloadReport')}
                            </Button>
                        )}
                    </div>
                </Stack>
            </Container>
            <Container fluid>
                <div className="mb-4">
                    <h4 className="mb-4">{eyeTestImagesData?.doctorName}</h4>

                    <Card className="prescription_doctor_test">
                        <Card.Body>
                            <Stack direction="horizontal">
                                <ListGroup.Item>
                                    <span>{t('city')}</span>
                                    <h5>{eyeTestImagesData?.doctorAddress}</h5>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                    <span>{t('AMnumber')}</span>
                                    <h5>
                                        {
                                            eyeTestImagesData?.infoDoctorIdOfRegistration
                                        }
                                    </h5>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                    <span>RPPS:</span>
                                    <h5>
                                        {
                                            eyeTestImagesData?.infoDoctorRppsNumber
                                        }
                                    </h5>
                                </ListGroup.Item>
                            </Stack>
                        </Card.Body>
                    </Card>
                </div>
                <div className="mb-4">
                    <Stack
                        direction="horizontal"
                        gap={3}
                        className="prescription_name_card mb-4"
                    >
                        <div className="">
                            <h4 className="mb-0">{eyeTestData?.patientName}</h4>
                        </div>
                        <div className="ms-md-auto">
                            {eyeTestData?.acteGratuit ? t('freeAct') : ''},{' '}
                            {eyeTestImagesData?.doctorCity},{' '}
                            {frenchFormatDate(eyeTestData?.createdDate)}{' '}
                        </div>
                    </Stack>

                    <Card className="mb-3 eye_test_symtoms">
                        <Card.Body>
                            <Card.Title>
                                {eyeTestData?.testDetails[7]?.add == '0.00' &&
                                eyeTestData?.testDetails[6]?.add == '0.00' ? (
                                    <>
                                        {eyeTestData.isContactLensType
                                            ? t('prescriptionLensTitle')
                                            : t('correctiveLensesAndFrames')}
                                    </>
                                ) : (
                                     <>
                                        {eyeTestData.isContactLensType
                                            ? t('prescriptionLensTitle')
                                            :  t('progressiveTitle')}
                                    </>
                                )}
                                *
                            </Card.Title>
                            <Table bordered>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>SPH</th>
                                        <th>CYL</th>
                                        <th>AXE</th>
                                        <th>ADD</th>
                                        {eyeTestData.isContactLensType ===
                                        false ? (
                                            <th>PD</th>
                                        ) : null}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>OD</td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                value={
                                                    eyeTestData?.testDetails[8]
                                                        ?.sph || ''
                                                }
                                                placeholder="Normal text"
                                                disabled
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                value={
                                                    eyeTestData?.testDetails[8]
                                                        ?.cyl || ''
                                                }
                                                placeholder="Normal text"
                                                disabled
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                value={
                                                    eyeTestData?.testDetails[8]
                                                        ?.axe || ''
                                                }
                                                placeholder="Normal text"
                                                disabled
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                value={
                                                    eyeTestData?.testDetails[8]
                                                        ?.add || ''
                                                }
                                                placeholder="Normal text"
                                                disabled
                                            />
                                        </td>
                                        {eyeTestData.isContactLensType ===
                                        false ? (
                                            <td>
                                                <Form.Control
                                                    type="text"
                                                    value={
                                                        eyeTestData
                                                            ?.testDetails[8]?.pd || ''
                                                    }
                                                    placeholder="Normal text"
                                                    disabled
                                                />
                                            </td>
                                        ) : null}
                                    </tr>
                                    <tr>
                                        <td>OG</td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                value={
                                                    eyeTestData?.testDetails[9]
                                                        ?.sph || ''
                                                }
                                                placeholder="Normal text"
                                                disabled
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                value={
                                                    eyeTestData?.testDetails[9]
                                                        ?.cyl || ''
                                                }
                                                placeholder="Normal text"
                                                disabled
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                value={
                                                    eyeTestData?.testDetails[9]
                                                        ?.axe || ''
                                                }
                                                placeholder="Normal text"
                                                disabled
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                value={
                                                    eyeTestData?.testDetails[9]
                                                        ?.add || ''
                                                }
                                                placeholder="Normal text"
                                                disabled
                                            />
                                        </td>
                                        {eyeTestData.isContactLensType ===
                                        false ? (
                                            <td>
                                                <Form.Control
                                                    type="text"
                                                    value={
                                                        eyeTestData
                                                            ?.testDetails[9]?.pd || ''
                                                    }
                                                    placeholder="Normal text"
                                                    disabled
                                                />
                                            </td>
                                        ) : null}
                                    </tr>
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </div>
                <div className="mb-4">
                    <h4 className="mb-4">{t('signature')}</h4>

                    <Card className="prescription_doctor_test mb-4">
                        <Card.Body>
                            {eyeTestImagesData?.sigimage ? (
                                <>
                                    <Stack direction="horizontal">
                                        <Image
                                            src={`${config.API_URL}/${eyeTestImagesData?.doctorSignature}`}
                                            width={200}
                                            alt="Doctor Signature"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.parentElement.textContent = eyeTestImagesData?.doctorName;
                                            }}
                                        />
                                    </Stack>
                                </>
                            ) : (
                                eyeTestImagesData?.doctorName
                            )}
                            <Stack
                                direction="horizontal"
                                gap={3}
                                className="mt-4"
                            >
                                <div className="">
                                    {eyeTestImagesData?.doctorIdImagePath ? (
                                        <>
                                            <Image
                                                src={`${config.API_URL}/${eyeTestImagesData?.doctorIdImagePath}`}
                                                width={200}
                                                alt="Doctor ID"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.textContent = eyeTestImagesData?.infoDoctorIdOfRegistration;
                                                }}
                                            />
                                        </>
                                    ) : (
                                        eyeTestImagesData?.infoDoctorIdOfRegistration
                                    )}
                                </div>
                                <div className="ms-md-auto">
                                    {eyeTestImagesData?.rpcsIdImagePath ? (
                                        <>
                                            <Image
                                                src={`${config.API_URL}/${eyeTestImagesData?.rpcsIdImagePath}`}
                                                width={200}
                                                alt="RPPS ID"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.textContent = eyeTestImagesData?.infoDoctorRppsNumber;
                                                }}
                                            />
                                        </>
                                    ) : (
                                        eyeTestImagesData?.infoDoctorRppsNumber
                                    )}{' '}
                                </div>
                            </Stack>
                        </Card.Body>
                    </Card>
                    <p>
                        {t('thisPrescriptionWasValidatedByDoctor')}{' '}
                        {eyeTestImagesData?.doctorName}{' '}
                        {t('viaATeleexpertiseProcedure')} <br /> {t('testID')} :{' '}
                        {eyeTestData?.testUniqueCode || 'N/A'}
                    </p>
                </div>
            </Container>
        </>
    );
};

export default Prescription;